// src/onboarding.js — Wizard de onboarding interactivo

import { auth } from './firebase.js';
import { cargarPerfil, guardarOnboardingCompletado } from './perfil-datos.js';

const STORAGE_PREFIX = 'onboarding_completado_v1';
const PADDING = 10;

let onboardingActivo = false;
let onboardingEsperando = false;

function getStorageKey(uid) {
    return `${STORAGE_PREFIX}_${uid}`;
}

async function onboardingYaVisto(uid) {
    if (!uid) return true;

    if (localStorage.getItem(getStorageKey(uid)) === '1') {
        return true;
    }

    const perfil = await cargarPerfil(uid);
    const visto = perfil?.preferencias?.onboardingCompletado === true;

    if (visto) {
        localStorage.setItem(getStorageKey(uid), '1');
    }

    return visto;
}

async function marcarOnboardingVisto() {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    localStorage.setItem(getStorageKey(uid), '1');
    await guardarOnboardingCompletado(uid);
}

function getLang() {
    return document.documentElement.lang === 'en' ? 'en' : 'es';
}

function L(paso) {
    return paso.labels[getLang()] ?? paso.labels.es;
}

// ============================================================
//  Helpers de visibilidad
// ============================================================

// Tiene dimensiones en el viewport (no depende de offsetParent, que falla
// para elementos dentro de position:fixed).
function esVisible(el) {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
}

// El panel requerido por el paso está abierto (tiene modal--visible).
function esPanelOk(paso) {
    if (!paso.panelRequired) return true;
    return document.getElementById(paso.panelRequired)
        ?.classList.contains('modal--visible') ?? false;
}

// El elemento es lo que está en el punto central del viewport
// (nada lo tapa — p.ej. un modal abierto encima).
function esClickeable(el) {
    if (!esVisible(el)) return false;
    const r = el.getBoundingClientRect();
    const cx = Math.round(r.left + r.width / 2);
    const cy = Math.round(r.top  + r.height / 2);
    const hit = document.elementFromPoint(cx, cy);
    return hit !== null && (hit === el || el.contains(hit));
}

function asegurarElementoEnVista(el) {
    if (!el || typeof el.scrollIntoView !== 'function') return;

    const r = el.getBoundingClientRect();
    const margen = 120;
    const fueraDeVista = r.top < margen || r.bottom > (window.innerHeight - margen);

    if (!fueraDeVista) return;

    el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
    });
}

// ============================================================
//  Definición de pasos
// ============================================================

const PASOS = [
    // ── 1 · Bienvenida ────────────────────────────────────────
    {
        tipo: 'info',
        labels: {
            es: { titulo: 'Bienvenido a Contest Manager', texto: 'Te guiaremos paso a paso para completar tu perfil y registrar tu primer concurso.' },
            en: { titulo: 'Welcome to Contest Manager', texto: 'We will guide you step by step to complete your profile and log your first contest.' },
        },
    },
    // ── 2 · Abrir editor de perfil ────────────────────────────
    {
        tipo: 'accion',
        targetSelector: '.btn-editar-tarjeta',
        event: 'click',
        labels: {
            es: { titulo: 'Editar perfil', texto: 'Haz clic en el lápiz de edición para abrir el formulario de perfil.' },
            en: { titulo: 'Edit profile', texto: 'Click the pencil icon to open the profile form.' },
        },
    },
    // ── 3 · Indicativo principal ──────────────────────────────
    {
        tipo: 'campo_requerido',
        targetSelector: '[data-field="indicativoOperador"]',
        validar: (el) => /^[A-Z0-9]{3,}$/i.test(el.value.trim()),
        labels: {
            es: { titulo: 'Indicativo principal', texto: 'Ingresa tu indicativo de operador (mínimo 3 caracteres, solo letras y números).' },
            en: { titulo: 'Main callsign', texto: 'Enter your operator callsign (minimum 3 characters, letters and numbers only).' },
        },
    },
    // ── 4 · Indicativos especiales ────────────────────────────
    {
        tipo: 'campo_opcional',
        targetSelector: '[data-field="indicativosEspeciales"]',
        labels: {
            es: { titulo: 'Indicativos especiales', texto: 'Puedes ingresar uno o varios indicativos especiales o de club, separados por comas. Es opcional.' },
            en: { titulo: 'Special callsigns', texto: 'You can enter one or more special or club callsigns, separated by commas. This is optional.' },
        },
    },
    // ── 5 · Club ──────────────────────────────────────────────
    {
        tipo: 'campo_opcional',
        targetSelector: '[data-field="club"]',
        labels: {
            es: { titulo: 'Club / Sociedad', texto: 'Indica el club o sociedad de radioaficionados al que perteneces (opcional).' },
            en: { titulo: 'Club / Society', texto: 'Enter the amateur radio club or society you belong to (optional).' },
        },
    },
    // ── 6 · País / DXCC ──────────────────────────────────────
    {
        tipo: 'campo_requerido',
        targetSelector: '.dxcc-selector input',
        // El widget escribe en el campo oculto; validamos sobre él.
        validar: () => (document.querySelector('[data-field="dxcc"]')?.value.trim() ?? '') !== '',
        usePoll: true,
        // Expande el spotlight para cubrir el dropdown cuando esté visible.
        getRect() {
            const selector = document.querySelector('.dxcc-selector');
            if (!selector) return null;
            const sr = selector.getBoundingClientRect();
            const dropdown = selector.querySelector('.dxcc-dropdown');
            if (!dropdown || dropdown.style.display === 'none') return null;
            const dr = dropdown.getBoundingClientRect();
            const top    = Math.min(sr.top,    dr.top);
            const left   = Math.min(sr.left,   dr.left);
            const right  = Math.max(sr.right,  dr.right);
            const bottom = Math.max(sr.bottom, dr.bottom);
            return { top, left, width: right - left, height: bottom - top };
        },
        labels: {
            es: { titulo: 'País / DXCC', texto: 'Busca y selecciona tu país o entidad DXCC. Escribe el nombre o el código y elige de la lista.' },
            en: { titulo: 'Country / DXCC', texto: 'Search and select your country or DXCC entity. Type the name or code and choose from the list.' },
        },
    },
    // ── 7 · QTH ───────────────────────────────────────────────
    {
        tipo: 'campo_requerido',
        targetSelector: '[data-field="qth"]',
        validar: (el) => el.value.trim().length >= 2,
        labels: {
            es: { titulo: 'QTH', texto: 'Ingresa tu localidad o ciudad de operación.' },
            en: { titulo: 'QTH', texto: 'Enter your operating locality or city.' },
        },
    },
    // ── 8 · Grid Locator (con link de ayuda) ─────────────────
    {
        tipo: 'campo_requerido',
        targetSelector: '[data-field="gridLocator"]',
        validar: (el) => {
            const s = el.value.trim();
            if (s.length !== 6) return false;
            const n = s.slice(0, 2).toUpperCase() + s.slice(2, 4) + s.slice(4, 6).toLowerCase();
            return /^[A-Z]{2}\d{2}[a-z]{2}$/.test(n);
        },
        labels: {
            es: {
                titulo: 'Grid Locator',
                textoHTML: 'Ingresa tu localizador Maidenhead (6 caracteres, formato AA00aa). Si no lo conoces, <a href="https://www.karhukoti.com/maidenhead-grid-square-locator-desktop-map/" target="_blank" rel="noopener noreferrer" class="onboarding-link">búscalo aquí</a>.',
            },
            en: {
                titulo: 'Grid Locator',
                textoHTML: 'Enter your Maidenhead grid locator (6 characters, format AA00aa). If you don\'t know it, <a href="https://www.karhukoti.com/maidenhead-grid-square-locator-desktop-map/" target="_blank" rel="noopener noreferrer" class="onboarding-link">find it here</a>.',
            },
        },
    },
    // ── 9 · Teléfono ──────────────────────────────────────────
    {
        tipo: 'campo_opcional',
        targetSelector: '[data-field="telefono"]',
        labels: {
            es: { titulo: 'Teléfono', texto: 'Puedes agregar un número de teléfono o WhatsApp. Es opcional.' },
            en: { titulo: 'Phone', texto: 'You can add a phone or WhatsApp number. This is optional.' },
        },
    },
    // ── 10 · Correo ───────────────────────────────────────────
    {
        tipo: 'campo_requerido',
        targetSelector: '[data-field="correoElectronico"]',
        validar: (el) => {
            const v = el.value.trim();
            const at = v.indexOf('@');
            return at > 0 && v.indexOf('.', at) > at + 1;
        },
        labels: {
            es: { titulo: 'Correo de contacto', texto: 'Ingresa tu correo electrónico de contacto.' },
            en: { titulo: 'Contact email', texto: 'Enter your contact email address.' },
        },
    },
    // ── 11 · QRZ ──────────────────────────────────────────────
    {
        tipo: 'campo_opcional',
        targetSelector: '[data-field="qrzCallsign"]',
        labels: {
            es: { titulo: 'QRZ', texto: 'Puedes asociar tu indicativo a tu perfil de QRZ.com. Es opcional y puedes dejarlo vacío.' },
            en: { titulo: 'QRZ', texto: 'You can link your callsign to your QRZ.com profile. This is optional.' },
        },
    },
    // ── 12 · Club Log ─────────────────────────────────────────
    {
        tipo: 'campo_opcional',
        targetSelector: '[data-field="clubLogCallsign"]',
        labels: {
            es: { titulo: 'Club Log', texto: 'Puedes asociar tu indicativo a Club Log. Es opcional y puedes dejarlo vacío.' },
            en: { titulo: 'Club Log', texto: 'You can link your callsign to Club Log. This is optional.' },
        },
    },
    // ── 13 · eQSL ─────────────────────────────────────────────
    {
        tipo: 'campo_opcional',
        targetSelector: '[data-field="eQslCallsign"]',
        labels: {
            es: { titulo: 'eQSL', texto: 'Puedes asociar tu indicativo a eQSL. Es opcional y puedes dejarlo vacío.' },
            en: { titulo: 'eQSL', texto: 'You can link your callsign to eQSL. This is optional.' },
        },
    },
    // ── 14 · Guardar perfil ───────────────────────────────────
    {
        tipo: 'accion',
        targetSelector: '.tarjeta-edit-bar .btn-guardar',
        event: 'click',
        labels: {
            es: { titulo: 'Guardar perfil', texto: 'Cuando estés listo, haz clic en "Guardar" para registrar tus datos.' },
            en: { titulo: 'Save profile', texto: 'When ready, click "Save" to store your data.' },
        },
    },
    // ── 15 · Abrir Configuración ──────────────────────────────
    {
        tipo: 'accion',
        targetId: 'btn-configuracion',
        event: 'click',
        labels: {
            es: { titulo: 'Configuración', texto: 'Haz clic aquí para abrir el panel de configuración y preferencias.' },
            en: { titulo: 'Settings', texto: 'Click here to open the settings and preferences panel.' },
        },
    },
    // ── 16 · Almacenamiento ───────────────────────────────────
    {
        tipo: 'campo_opcional',
        targetSelector: '#panel-perfil .pref-grupo:has(.pref-grupo__titulo[data-i18n="perfil.seccion.almacenamiento"])',
        panelRequired: 'panel-perfil',
        getRect() {
            const titulo   = document.querySelector('#panel-perfil .pref-grupo__titulo[data-i18n="perfil.seccion.almacenamiento"]');
            const rows     = document.querySelectorAll('#panel-perfil .pref-grupo:has(.pref-grupo__titulo[data-i18n="perfil.seccion.almacenamiento"]) > .pref-row');
            if (!titulo || !rows.length) return null;

            const lastRow     = rows[rows.length - 1];
            const lastActions = lastRow.querySelector('.pref-row__actions') ?? lastRow;
            const tr     = titulo.getBoundingClientRect();
            const lr     = lastRow.getBoundingClientRect();
            const ar     = lastActions.getBoundingClientRect();
            
            const panel = document.querySelector('#panel-perfil .panel-perfil');
            const pr = panel.getBoundingClientRect();
            
            const top    = tr.top    - 4;
            const left  = pr.left   + 20;
            const right = pr.right  - 20;
            const bottom = lr.bottom + 6;
            return { top, left, width: right - left, height: bottom - top };
        },
        labels: {
            es: { titulo: 'Almacenamiento', texto: 'Elige dónde guardar tus archivos: tu Google Drive personal o el almacenamiento de Contest Manager.' },
            en: { titulo: 'Storage', texto: 'Choose where to save your files: your personal Google Drive or Contest Manager storage.' },
        },
    },
    // ── 17 · Tema ─────────────────────────────────────────────
    {
        tipo: 'campo_opcional',
        targetSelector: '#perfil-tema-switch + .pref-switch__track',
        panelRequired: 'panel-perfil',
        labels: {
            es: { titulo: 'Tema / Apariencia', texto: 'Activa o desactiva el tema oscuro según tu preferencia.' },
            en: { titulo: 'Theme / Appearance', texto: 'Toggle dark mode on or off according to your preference.' },
        },
    },
    // ── 18 · Notificaciones ───────────────────────────────────
    {
        tipo: 'campo_opcional',
        targetSelector: '#panel-perfil .pref-grupo:has(#notif-general)',
        panelRequired: 'panel-perfil',
        getRect() {
            const grupo = document.querySelector('#panel-perfil .pref-grupo:has(#notif-general)');
            if (!grupo) return null;
            const panel = document.querySelector('#panel-perfil .panel-perfil');
            if (!panel) return null;
            const gr = grupo.getBoundingClientRect();
            const pr = panel.getBoundingClientRect();
            return {
                top:    gr.top    - 4,
                left:   pr.left   + 20,
                width:  pr.right  - pr.left - 40,
                height: gr.height + 8,
            };
        },
        labels: {
            es: { titulo: 'Notificaciones', texto: 'Activa las notificaciones para recibir avisos sobre resultados y certificados.' },
            en: { titulo: 'Notifications', texto: 'Enable notifications to receive alerts about results and certificates.' },
        },
    },
    // ── 19 · Cerrar panel ─────────────────────────────────────
    {
        tipo: 'accion',
        targetId: 'btn-cerrar-perfil',
        panelRequired: 'panel-perfil',
        event: 'click',
        labels: {
            es: { titulo: 'Volver al inicio', texto: 'Haz clic en ✕ para cerrar el panel y volver al dashboard.' },
            en: { titulo: 'Back to dashboard', texto: 'Click ✕ to close the panel and return to the dashboard.' },
        },
    },
    // ── 20 · Nuevo concurso ───────────────────────────────────
    // autoClosePanel: si #panel-perfil cubre el botón, se cierra automáticamente.
    {
        tipo: 'accion',
        targetId: 'btn-nuevo-concurso',
        event: 'click',
        autoClosePanel: true,
        labels: {
            es: { titulo: 'Primer concurso', texto: 'Haz clic en "+ Nuevo concurso" para registrar tu primera participación.' },
            en: { titulo: 'First contest', texto: 'Click "+ New contest" to log your first participation.' },
        },
    },
    // ── 21 · Cierre ───────────────────────────────────────────
    {
        tipo: 'info',
        labels: {
            es: { titulo: '¡Todo listo!', texto: 'Tu perfil está configurado. Ahora puedes explorar todas las funciones de la plataforma.' },
            en: { titulo: 'All set!', texto: 'Your profile is configured. You can now explore all features of the platform.' },
        },
    },
];

// ============================================================
//  Estado
// ============================================================

let pasoActual = 0;
let transitioning = false;
let overlay, spotlight, tooltip, btnSig, btnPrev, btnCerrar, stepCounter, btnTemporal, btnNunca;
let cleanupFns = [];
let limpiarBloqueador = null;

function addCleanup(fn) { cleanupFns.push(fn); }
function runCleanup() { cleanupFns.forEach(fn => fn()); cleanupFns = []; }

// Bloquea todos los clics/toques fuera del tooltip y del target del paso activo.
function crearBloqueadorInteraccion() {
    const EVENTOS = ['mousedown', 'click', 'touchstart', 'touchend'];
    function interceptar(e) {
        if (tooltip && tooltip.contains(e.target)) return;
        // Permitir interacción con cualquier modal que esté abierto (clase modal--visible)
        if (e.target.closest?.('.modal--visible')) return;
        const paso = PASOS[pasoActual];
        const el = getTarget(paso);
        if (el) {
            // Para widgets compuestos (ej. dxcc-selector + dropdown) permitir el contenedor
            const zona = el.closest?.('.dxcc-selector') ?? el;
            if (zona === e.target || zona.contains(e.target)) return;
        }
        window.dispatchEvent(new CustomEvent('cm:close-info-tooltips'));
        e.stopImmediatePropagation();
        e.preventDefault();
    }
    EVENTOS.forEach(ev => document.addEventListener(ev, interceptar, true));
    return () => EVENTOS.forEach(ev => document.removeEventListener(ev, interceptar, true));
}

// ============================================================
//  DOM helpers
// ============================================================

function getTarget(paso) {
    if (paso.targetSelector) return document.querySelector(paso.targetSelector);
    if (paso.targetId) return document.getElementById(paso.targetId);
    return null;
}

function posicionarSpotlight(el, rectOverride) {
    if (!el) {
        spotlight.style.display = 'none';
        overlay.classList.add('onboarding-overlay--dim');
        return;
    }
    overlay.classList.remove('onboarding-overlay--dim');
    const r = rectOverride ?? el.getBoundingClientRect();
    spotlight.style.display = 'block';
    spotlight.style.top    = `${r.top    - PADDING}px`;
    spotlight.style.left   = `${r.left   - PADDING}px`;
    spotlight.style.width  = `${r.width  + PADDING * 2}px`;
    spotlight.style.height = `${r.height + PADDING * 2}px`;
}

function posicionarTooltip(el) {
    tooltip.style.top = '';
    tooltip.style.left = '';
    tooltip.style.transform = '';
    tooltip.dataset.arrow = 'none';

    if (!el) {
        tooltip.style.top = '50%';
        tooltip.style.left = '50%';
        tooltip.style.transform = 'translate(-50%, -50%)';
        return;
    }

    const r = el.getBoundingClientRect();
    const tw = tooltip.offsetWidth || 320;
    const th = tooltip.offsetHeight || 160;
    const margin = PADDING + 12;

        const esDxccSelector = !!el.closest?.('.dxcc-selector');

    const fitsBelow = r.bottom + margin + th < window.innerHeight;
    const fitsAbove = r.top - margin - th > 12;

    let top;

    if (esDxccSelector && fitsAbove) {
        top = r.top - margin - th;
        tooltip.dataset.arrow = 'down';
    } else {
        top = fitsBelow ? r.bottom + margin : r.top - margin - th;
        tooltip.dataset.arrow = fitsBelow ? 'up' : 'down';
    }

    let left = r.left + r.width / 2 - tw / 2;
    const clampedLeft = Math.max(12, Math.min(left, window.innerWidth - tw - 12));

    const arrowX = Math.max(20, Math.min(Math.round(r.left + r.width / 2 - clampedLeft), tw - 20));
    tooltip.style.setProperty('--arrow-x', `${arrowX}px`);

    tooltip.style.top  = `${top}px`;
    tooltip.style.left = `${clampedLeft}px`;
}

// ============================================================
//  Validación
// ============================================================

function evaluarValidez(paso) {
    if (paso.tipo !== 'campo_requerido') return true;
    const el = getTarget(paso);
    if (!el || !esVisible(el) || !esPanelOk(paso)) return false;
    return paso.validar(el);
}

function actualizarBtnSig(paso) {
    const valido = evaluarValidez(paso);
    btnSig.disabled = !valido;
    btnSig.classList.toggle('onboarding-sig--disabled', !valido);
}

// ============================================================
//  Navegación con fade
// ============================================================

function irAPaso(index) {
    if (transitioning) return;
    if (index >= PASOS.length) { cerrar(true); return; }
    if (index < 0) return;
    transitioning = true;
    tooltip.classList.add('onboarding-tooltip--fade');
    setTimeout(() => {
        pasoActual = index;
        renderPaso(index);
        requestAnimationFrame(() => { transitioning = false; });
    }, 130);
}

function siguiente() { irAPaso(pasoActual + 1); }
function anterior()  { irAPaso(pasoActual - 1); }

async function cerrar(completado = false) {
    runCleanup();

    limpiarBloqueador?.();
    limpiarBloqueador = null;
    document.body.classList.remove('onboarding-activo');

    if (completado) {
        await marcarOnboardingVisto();
    }

    overlay?.remove();
    overlay = null;

    onboardingActivo = false;

    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onScroll, true);
}

// ============================================================
//  Render de paso
// ============================================================

function renderPaso(index) {
    runCleanup();

    const paso = PASOS[index];
    const lbl  = L(paso);
    const lang = getLang();

    tooltip.querySelector('.onboarding-titulo').textContent = lbl.titulo;
    const textoEl = tooltip.querySelector('.onboarding-texto');
    if (lbl.textoHTML) {
        textoEl.innerHTML = lbl.textoHTML;
    } else {
        textoEl.textContent = lbl.texto ?? '';
    }

    stepCounter.textContent = `${index + 1} / ${PASOS.length}`;

    const esPrimero = index === 0;
    const esUltimo  = index === PASOS.length - 1;
    const esAccion  = paso.tipo === 'accion';

    btnPrev.style.display = esPrimero ? 'none' : '';
    btnPrev.textContent   = lang === 'en' ? '← Back' : '← Anterior';

    btnSig.style.display = esAccion ? 'none' : '';
    if (!esAccion) {
        btnSig.textContent = esUltimo
            ? (lang === 'en' ? 'Finish' : 'Finalizar')
            : (lang === 'en' ? 'Next →' : 'Siguiente →');
    }

    // Bloque de opciones: oculto en el último paso (Finalizar cubre ambas acciones)
    const opcionesRow = tooltip.querySelector('.onboarding-opciones');
    if (opcionesRow) opcionesRow.style.display = esUltimo ? 'none' : '';
    btnTemporal.textContent = lang === 'en' ? 'Remind me later'  : 'Recordármelo más tarde';
    btnNunca.textContent    = lang === 'en' ? "Don't show again" : 'No volver a mostrar';

    if (esAccion) {
        // ── Pasos de acción ──────────────────────────────────
        posicionarSpotlight(null);
        // Tooltip permanece oculto (--fade activo) hasta que el poll ubique el target.

        let detenido = false;
        const poll = setInterval(() => {
            if (detenido) return;
            const el = getTarget(paso);
            if (!el || !esVisible(el) || !esPanelOk(paso)) return;

            // Para pasos con autoClosePanel: si el elemento no es clickeable
            // (algo lo tapa — p.ej. el panel de config), cerramos el panel
            // automáticamente y reintentamos en el siguiente tick.
            if (paso.autoClosePanel && !esClickeable(el)) {
                document.getElementById('panel-perfil')?.classList.remove('modal--visible');
                return;
            }

            detenido = true;
            clearInterval(poll);
            posicionarSpotlight(el, paso.getRect?.());
            posicionarTooltip(el);
            tooltip.classList.remove('onboarding-tooltip--fade');
            const handler = () => siguiente();
            el.addEventListener(paso.event, handler, { once: true });
            addCleanup(() => el.removeEventListener(paso.event, handler));
        }, 300);
        addCleanup(() => { detenido = true; clearInterval(poll); });

    } else {
        // ── Pasos info / campo ───────────────────────────────
        const el = getTarget(paso);
        const tieneTarget = paso.targetId || paso.targetSelector;
        const ready = el && esVisible(el) && esPanelOk(paso);

            if (ready) {
                asegurarElementoEnVista(el);
                posicionarSpotlight(el, paso.getRect?.());
                requestAnimationFrame(() => {
                posicionarSpotlight(el, paso.getRect?.());
                posicionarTooltip(el);
                tooltip.classList.remove('onboarding-tooltip--fade');
            });
            if (paso.getRect) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        posicionarSpotlight(el, paso.getRect());
                        posicionarTooltip(el);
                    });
                });
                const tid = setTimeout(() => {
                    posicionarSpotlight(el, paso.getRect());
                    posicionarTooltip(el);
                }, 300);
                addCleanup(() => clearTimeout(tid));
                const panelEl = paso.panelRequired && document.getElementById(paso.panelRequired);
                if (panelEl) {
                    const onTransEnd = () => {
                        posicionarSpotlight(el, paso.getRect());
                        posicionarTooltip(el);
                    };
                    panelEl.addEventListener('transitionend', onTransEnd, { once: true });
                    addCleanup(() => panelEl.removeEventListener('transitionend', onTransEnd));
                }
            }
        } else if (tieneTarget) {
            // Target no disponible aún: tooltip permanece oculto hasta que el poll lo ubique.
            posicionarSpotlight(null);
        } else {
            // Paso info sin target: centrar y mostrar directamente.
            posicionarSpotlight(null);
            requestAnimationFrame(() => {
                posicionarTooltip(null);
                tooltip.classList.remove('onboarding-tooltip--fade');
            });
        }

        actualizarBtnSig(paso);

        if (ready && paso.tipo === 'campo_requerido') {
            const onInput = () => actualizarBtnSig(paso);
            el.addEventListener('input', onInput);
            addCleanup(() => el.removeEventListener('input', onInput));
            if (paso.usePoll) {
                const elRef = el;
                const iv = setInterval(() => {
                    actualizarBtnSig(paso);
                    if (paso.getRect) posicionarSpotlight(elRef, paso.getRect());
                }, 150);
                addCleanup(() => clearInterval(iv));
            }
        }

        if (!ready && tieneTarget) {
            let stopped = false;
            const poll = setInterval(() => {
                if (stopped) return;
                const found = getTarget(paso);
                if (!found || !esVisible(found) || !esPanelOk(paso)) return;
                                stopped = true;
                clearInterval(poll);
                asegurarElementoEnVista(found);
                posicionarSpotlight(found, paso.getRect?.());
                requestAnimationFrame(() => {
                    posicionarSpotlight(found, paso.getRect?.());
                    posicionarTooltip(found);
                    tooltip.classList.remove('onboarding-tooltip--fade');
                });
                if (paso.getRect) {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            posicionarSpotlight(found, paso.getRect());
                            posicionarTooltip(found);
                        });
                    });
                    const tid2 = setTimeout(() => {
                        posicionarSpotlight(found, paso.getRect());
                        posicionarTooltip(found);
                    }, 300);
                    addCleanup(() => clearTimeout(tid2));
                    const panelEl = paso.panelRequired && document.getElementById(paso.panelRequired);
                    if (panelEl) {
                        const onTransEnd = () => {
                            posicionarSpotlight(found, paso.getRect());
                            posicionarTooltip(found);
                        };
                        panelEl.addEventListener('transitionend', onTransEnd, { once: true });
                        addCleanup(() => panelEl.removeEventListener('transitionend', onTransEnd));
                    }
                }
                actualizarBtnSig(paso);
                if (paso.tipo === 'campo_requerido') {
                    const onInput = () => actualizarBtnSig(paso);
                    found.addEventListener('input', onInput);
                    addCleanup(() => found.removeEventListener('input', onInput));
                    if (paso.usePoll) {
                        const foundRef = found;
                        const iv = setInterval(() => {
                            actualizarBtnSig(paso);
                            if (paso.getRect) posicionarSpotlight(foundRef, paso.getRect());
                        }, 150);
                        addCleanup(() => clearInterval(iv));
                    }
                }
            }, 200);
            addCleanup(() => { stopped = true; clearInterval(poll); });
        }
    }
}

function onResize() {
    const paso = PASOS[pasoActual];
    const el = getTarget(paso);
    if (el && esVisible(el) && esPanelOk(paso)) {
        posicionarSpotlight(el, paso.getRect?.());
        posicionarTooltip(el);
    }
}

function onScroll() {
    const paso = PASOS[pasoActual];
    const el = getTarget(paso);
    if (el && esVisible(el) && esPanelOk(paso)) {
        posicionarSpotlight(el, paso.getRect?.());
        posicionarTooltip(el);
    }
}

// ============================================================
//  Construcción del DOM
// ============================================================

function construirDOM() {
    overlay = document.createElement('div');
    overlay.className = 'onboarding-overlay';

    spotlight = document.createElement('div');
    spotlight.className = 'onboarding-spotlight';

    tooltip = document.createElement('div');
    tooltip.className = 'onboarding-tooltip';
    tooltip.innerHTML = `
        <button class="onboarding-cerrar" aria-label="Cerrar">✕</button>
        <p class="onboarding-titulo"></p>
        <p class="onboarding-texto"></p>
        <div class="onboarding-footer">
            <span class="onboarding-step-counter"></span>
            <div class="onboarding-nav">
                <button class="onboarding-prev"></button>
                <button class="onboarding-sig btn-nuevo"></button>
            </div>
        </div>
        <div class="onboarding-opciones">
            <button class="onboarding-btn-temporal"></button>
            <span class="onboarding-opciones__sep" aria-hidden="true">·</span>
            <button class="onboarding-btn-nunca"></button>
        </div>`;

    stepCounter = tooltip.querySelector('.onboarding-step-counter');
    btnSig      = tooltip.querySelector('.onboarding-sig');
    btnPrev     = tooltip.querySelector('.onboarding-prev');
    btnCerrar   = tooltip.querySelector('.onboarding-cerrar');
    btnTemporal = tooltip.querySelector('.onboarding-btn-temporal');
    btnNunca    = tooltip.querySelector('.onboarding-btn-nunca');

    btnSig.addEventListener('click', siguiente);
    btnPrev.addEventListener('click', anterior);
    btnCerrar.addEventListener('click', () => cerrar(false));
    btnTemporal.addEventListener('click', () => cerrar(false));
    btnNunca.addEventListener('click', () => cerrar(true));

    overlay.appendChild(spotlight);
    overlay.appendChild(tooltip);
    document.body.appendChild(overlay);

    document.body.classList.add('onboarding-activo');
    limpiarBloqueador = crearBloqueadorInteraccion();
}

// ============================================================
//  Entry point
// ============================================================

export function initOnboarding(opciones = {}) {
    const forzar = opciones?.forzar === true;

    if (onboardingActivo || onboardingEsperando) return;

    onboardingEsperando = true;

    const esperar = async () => {
        const activo = document.getElementById('vista-dashboard')?.classList.contains('vista--activa');
        const uid = auth.currentUser?.uid;

        if (!activo || !uid) {
            setTimeout(esperar, 300);
            return;
        }

        if (!forzar && await onboardingYaVisto(uid)) {
            onboardingEsperando = false;
            return;
        }

        onboardingEsperando = false;
        onboardingActivo = true;

        pasoActual = 0;
        transitioning = false;

        construirDOM();
        renderPaso(0);

        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onScroll, true);
    };

    esperar();
}
